import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import SEO from '@/components/SEO';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const categories: FAQCategory[] = [
  {
    title: 'Domain Availability',
    items: [
      {
        q: 'How do I check if a domain name is available?',
        a: 'Type your keyword into the DomBase search bar and we instantly scan 40+ TLDs simultaneously. Results show availability, pricing from multiple registrars, WHOIS data, and a domain score — all without requiring an account.',
      },
      {
        q: 'How accurate is the availability check?',
        a: 'DomBase queries live registrar APIs and WHOIS data in real time. Results are cached briefly (available domains for 2 hours, registered domains for 24 hours) to keep response times fast while maintaining accuracy.',
      },
      {
        q: 'Can I check multiple domains at once?',
        a: 'Yes. When you search a keyword, DomBase automatically scans it across all major TLDs (.com, .io, .ai, .co, .net, .org, and many more) in parallel. You can also search a specific domain like "example.com" to get a detailed single-domain result.',
      },
      {
        q: 'What TLDs does DomBase cover?',
        a: 'DomBase scans 40+ TLDs including .com, .io, .ai, .co, .net, .org, .app, .dev, .xyz, .me, .gg, .so, .sh, .cloud, .finance, .tech, .store, and more. The set is curated to focus on TLDs relevant to startups and tech companies.',
      },
    ],
  },
  {
    title: 'WHOIS & Registration',
    items: [
      {
        q: 'What is WHOIS and why does it matter?',
        a: 'WHOIS is a public database that stores registration information for every domain name — including the registrar, registration date, expiry date, and sometimes owner contact details. It helps you verify who owns a domain, when it was registered, and when it expires (useful for catching expiring domains).',
      },
      {
        q: 'Why is WHOIS data sometimes hidden?',
        a: "Domain registrars offer WHOIS privacy protection (sometimes called WHOIS guard or domain privacy) which replaces the owner's personal details with the registrar's contact info. Most registrars include this for free. If you see proxy/privacy service data, the domain is registered but the owner has opted for privacy.",
      },
      {
        q: 'What does the registration date tell me?',
        a: 'An older registration date signals that a domain has been held for a long time — often a positive trust signal for Google and buyers. A domain registered 10+ years ago is typically seen as more authoritative than one registered last month.',
      },
    ],
  },
  {
    title: 'Domain Valuation',
    items: [
      {
        q: 'How is a domain name valued?',
        a: 'DomBase calculates an estimated value based on four factors: length (shorter = more valuable), keyword strength (dictionary words and popular tech terms score higher), TLD (.com commands a premium), and memorability (pronounceable, no hyphens or numbers). The score combines into an estimated price range.',
      },
      {
        q: 'Why are short domain names worth more?',
        a: "Short domains (3-6 characters) are inherently scarce — there are only so many 3-letter .com combinations, and most were registered decades ago. They're easier to remember, type, and brand, which makes them more valuable for businesses.",
      },
      {
        q: 'How accurate is the DomBase valuation?',
        a: "DomBase's valuation is an algorithmic estimate based on public pricing signals — not an appraisal. For high-value domains ($10k+), consult a domain broker or use a dedicated appraisal service like Estibot. The DomBase score is best used for relative comparison across your search results.",
      },
    ],
  },
  {
    title: 'Pricing',
    items: [
      {
        q: 'Why do different registrars charge different prices?',
        a: 'Registrars compete on price and add varying fees for services like WHOIS privacy, DNS management, and email. DomBase pulls prices from GoDaddy, Namecheap, and Name.com so you can compare at a glance. The first-year price is often discounted; check the renewal price before committing.',
      },
      {
        q: 'What is a premium domain price?',
        a: "Some TLD registries (like Donuts, Radix, or the .io and .ai registries) designate certain domains as 'premium' and charge elevated registration prices — sometimes $100–$5,000+ at the registry level. DomBase flags these so you aren't surprised at checkout.",
      },
      {
        q: 'Are .com domains always the most expensive?',
        a: '.com registration fees are typically $10–$15/year at standard price. However, aftermarket .com domains (bought from a current owner, not at registration) can cost thousands to millions. DomBase shows registration prices for available domains only.',
      },
    ],
  },
  {
    title: 'Brand Risk',
    items: [
      {
        q: 'What does brand risk mean for a domain?',
        a: 'Brand risk measures how similar a domain name is to an existing well-known brand. A high brand risk score means the domain could be confused with a trademark holder — raising legal risk (UDRP disputes, cease-and-desist letters) even if the domain is technically available to register.',
      },
      {
        q: 'How does DomBase detect brand risk?',
        a: 'DomBase uses Damerau-Levenshtein distance (edit distance) to compare your domain against a database of 200+ known brands. It also checks for typosquatting patterns (swapped letters, added characters) and cross-TLD ownership (whether the same keyword is owned in .com when you search .io).',
      },
      {
        q: 'Can I safely register a domain with medium brand risk?',
        a: "Medium risk means there's some similarity to an existing brand but it's not a clear typosquat. Always review the flagged brand(s) manually. If you're building a product in the same space as the flagged brand, reconsider. If the similarity is coincidental and you have a distinct product, the risk is lower — but consult a lawyer for certainty.",
      },
    ],
  },
  {
    title: 'Expiring Domains',
    items: [
      {
        q: 'What is an expiring domain and how do I buy one?',
        a: "An expiring domain is one whose current owner has not renewed their registration before the expiry date. After expiry, there is a grace period (30–90 days depending on the registrar) before it's released to the public. Expiring domains can be backordered through services like SnapNames, NameJet, or GoDaddy Auctions — or registered at standard price once they drop.",
      },
      {
        q: 'Why are expiring domains valuable?',
        a: 'A domain that has been registered for years may have accumulated backlinks, organic search traffic, and domain authority. Acquiring it gives you a head start compared to registering a fresh domain. The DomBase expiring domains list shows domains due to expire soon across all TLDs.',
      },
      {
        q: 'How do I watch an expiring domain?',
        a: 'Add any domain to your DomBase Watchlist and we will automatically re-check it on every visit. You can build a personal list of domains to monitor without creating an account — your watchlist is stored locally in your browser.',
      },
    ],
  },
];

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: categories.flatMap(cat =>
    cat.items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    }))
  ),
};

function AccordionItem({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-start justify-between gap-4 py-4 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-gray-900 leading-snug">{item.q}</span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="text-sm text-gray-500 leading-relaxed pb-4">{item.a}</p>
      )}
    </div>
  );
}

const FAQ: React.FC = () => {
  return (
    <>
      <SEO
        title="Domain Name FAQ — DomBase"
        description="Answers to the most common questions about domain availability, WHOIS, pricing, brand risk, valuation, and expiring domains."
        canonical="/faq"
        ogType="website"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'FAQ', url: '/faq' },
        ]}
      />

      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">Domain Name FAQ</h1>
          <p className="text-sm text-gray-400 mt-1">Common questions about domain availability, pricing, WHOIS, and brand risk.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {categories.map(cat => (
          <section key={cat.title}>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{cat.title}</h2>
            <div className="bg-white border border-gray-200 rounded-md px-4">
              {cat.items.map(item => (
                <AccordionItem key={item.q} item={item} />
              ))}
            </div>
          </section>
        ))}

        <p className="text-sm text-gray-400 text-center pt-4">
          Ready to find your domain?{' '}
          <a href="/" className="text-blue-500 hover:text-blue-600 font-medium">Search now →</a>
        </p>
      </div>
    </>
  );
};

export default FAQ;
